import showToast from "@utils/toast";
import React, { useState } from "react";
import { updateRequest } from "src/service/api-services/api-services";
import { API_URL } from "src/service/config";
import { GraphicApiResponse, IEditGraphicModal } from "src/types/graphic";

export default function EditGraphicModal({
  setIsModalOpen,
  description,
  graphicsKeywords,
  id,
}: IEditGraphicModal) {
  const [isLoading, setLoading] = useState<Boolean>(false);
  const [keywords, setKeywords] = useState<string[]>(graphicsKeywords);
  const [newKeyword, setNewKeyword] = useState("");

  const [data, setData] = useState({
    description,
    keyWords: keywords,
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      id: id,
      description: data.description,
      keyWords: keywords,
    };

    try {
      const response = await updateRequest<GraphicApiResponse>(
        `${API_URL}/graphics`,
        payload
      );
      if (response.status === "success") {
        setLoading(false);
        setIsModalOpen(false);
        showToast("Graphics was updated successfully!", true);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Apologies, an error occurred updating graphics!";
      setLoading(false);
      showToast(message, false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40">
      <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h2 className="text-lg font-semibold">Edit Graphic</h2>
          <button
            onClick={handleModalClose}
            className="text-gray-600 hover:text-black"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={data.description}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-brand"
              rows={3}
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keywords
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {keywords.map((tag, i) => (
                <span
                  key={i}
                  className="flex items-center bg-blue-100 text-brand text-xs px-2 py-1 rounded-full"
                >
                  {tag}
                  <button
                    onClick={() =>
                      setKeywords(keywords.filter((_, index) => index !== i))
                    }
                    className="ml-1 text-brand"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={newKeyword}
              onChange={(e) => setNewKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  if (newKeyword.trim()) {
                    setKeywords([...keywords, newKeyword.trim()]);
                    setNewKeyword("");
                  }
                }
              }}
              placeholder="Type keyword and press enter"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>

          <button
            disabled={isLoading ? true : false}
            type="submit"
            className="w-full bg-brand cursor-pointer text-white py-2 rounded font-medium hover:bg-brand-dark transition"
          >
            {isLoading ? "Updating...." : "Click to edit"}
          </button>
        </form>
      </div>
    </div>
  );
}
